import { useState, useEffect, useCallback } from "react";
import { db } from "../firebase";
import {
  collection,
  onSnapshot,
  doc,
  query,
  where,
  updateDoc,
} from "firebase/firestore";
import { AGENCY_ACTIVE } from "../constants";
import { formatError } from "../utils/formatError";
import { fromDoc } from "../utils/firestoreDoc";
import logger from "../utils/logger";
import type { Agency } from "../types";

/**
 * Loads the current user's agency document for branding and config.
 */
export function useAgency(agencyId: string | null) {
  const [agency, setAgency] = useState<Agency | null>(null);
  const [loadedFor, setLoadedFor] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Derived: loading iff we have an agencyId and haven't received a snapshot for it yet.
  // Avoids `setLoading` in the effect body (cascading-renders anti-pattern).
  const loading = !!agencyId && loadedFor !== agencyId;

  useEffect(() => {
    if (!agencyId) return;
    setError(null);
    const unsubscribe = onSnapshot(
      doc(db, "agencies", agencyId),
      (snap) => {
        if (snap.exists()) {
          setAgency(fromDoc<Agency>(snap));
        } else {
          setAgency(null);
        }
        setLoadedFor(agencyId);
      },
      (err) => {
        logger.error("Failed to load agency:", err);
        setError(formatError(err));
        setLoadedFor(agencyId);
      }
    );
    return () => unsubscribe();
  }, [agencyId]);

  const updateAgency = useCallback(async (updates: Partial<Agency>) => {
    if (!agencyId) return;
    try {
      await updateDoc(doc(db, "agencies", agencyId), updates);
    } catch (err) {
      throw new Error(formatError(err));
    }
  }, [agencyId]);

  return { agency, loading, error, updateAgency };
}

/**
 * Loads all active agencies (used on the registration screen).
 * Test-environment agencies are filtered out client-side (a `!=` query would
 * also drop every agency that has no `isTestData` field at all).
 */
export function useActiveAgencies() {
  const [agencies, setAgencies] = useState<Agency[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const q = query(
      collection(db, "agencies"),
      where("status", "==", AGENCY_ACTIVE)
    );
    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        setAgencies(snapshot.docs.map((d) => fromDoc<Agency>(d)).filter((a) => a.isTestData !== true));
        setLoading(false);
      },
      (err) => {
        logger.error("Failed to load agencies:", err);
        setLoading(false);
      }
    );
    return () => unsubscribe();
  }, []);

  return { agencies, loading };
}
